/**
 * CRM Data Table Component
 *
 * Reusable data table component with modern styling, pagination, column customization,
 * and row selection. Based on the provided design pattern.
 */

'use client'

import * as React from 'react'
import { IconChevronDown, IconLayoutColumns, IconPlus, IconChevronUp, IconSelector } from '@tabler/icons-react'
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  type VisibilityState
} from '@tanstack/react-table'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '@/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { useAtomValue } from 'jotai'
import { sidekickToolActiveAtom } from '@/atoms/uiAtoms'

function TableRowWithSidekickLoader<TData>({
  row,
  sidekickEntityType,
  tableColumns,
  onRowClick
}: {
  row: Row<TData>
  sidekickEntityType?: 'lead' | 'contact' | 'company'
  tableColumns: unknown[]
  onRowClick?: (row: TData) => void
}) {
  const active = useAtomValue(sidekickToolActiveAtom)
  const entityId = (row.original as { id?: string })?.id
  const isActive =
    sidekickEntityType &&
    entityId &&
    active?.entityType === sidekickEntityType &&
    active?.entityId === entityId

  return (
    <TableRow
      data-state={row.getIsSelected() && 'selected'}
      className={onRowClick ? "relative cursor-pointer" : "relative"}
      onClick={() => onRowClick?.(row.original)}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
      {isActive && (
        <TableCell
          colSpan={tableColumns.length}
          className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-[2px]"
        >
          <span className="text-xs text-muted-foreground">Updating…</span>
        </TableCell>
      )}
    </TableRow>
  )
}

interface CRMDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  enableRowSelection?: boolean
  enableColumnCustomization?: boolean
  enableAddButton?: boolean
  addButtonLabel?: string
  onAddClick?: () => void
  emptyMessage?: string
  getRowId?: (row: TData) => string
  /** Controlled row selection: selected row ids. When set, table selection is driven by parent. */
  selectedRowIds?: string[]
  /** Called when row selection changes. Use with selectedRowIds for controlled selection + bulk actions. */
  onRowSelectionChange?: (selectedRowIds: string[]) => void
  // Server-side pagination props
  totalCount?: number
  pageIndex?: number
  pageSize?: number
  onPageChange?: (pageIndex: number) => void
  onPageSizeChange?: (pageSize: number) => void
  /** When set, wraps each row in SidekickLoaderBoundary for tool-active overlay */
  sidekickEntityType?: 'lead' | 'contact' | 'company'
  /** When set with server-side pagination, header "Select all" selects all rows across pages; parent should fetch all IDs and call onRowSelectionChange */
  onSelectAllPagesRequested?: () => void
  /** Called when a table row is clicked (not on checkbox or action buttons) */
  onRowClick?: (row: TData) => void
}

export function CRMDataTable<TData, TValue>({
  columns,
  data,
  enableRowSelection = true,
  enableColumnCustomization = true,
  enableAddButton = false,
  addButtonLabel = 'Add',
  onAddClick,
  emptyMessage = 'No results.',
  getRowId,
  selectedRowIds,
  onRowSelectionChange,
  // Server-side pagination props
  totalCount,
  pageIndex: externalPageIndex,
  pageSize: externalPageSize,
  onPageChange,
  onPageSizeChange,
  sidekickEntityType,
  onSelectAllPagesRequested,
  onRowClick
}: CRMDataTableProps<TData, TValue>) {
  const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({})
  const getRowIdFn =
    getRowId || ((row: TData) => (row as { id?: string }).id?.toString() ?? String(row))
  const isControlledSelection = selectedRowIds !== undefined
  const rowSelection = isControlledSelection
    ? Object.fromEntries((selectedRowIds ?? []).map((id) => [id, true]))
    : internalRowSelection
  const handleRowSelectionChange = React.useCallback(
    (updater: React.SetStateAction<Record<string, boolean>>) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater
      const ids = Object.keys(next).filter((k) => next[k])
      onRowSelectionChange?.(ids)
      if (!isControlledSelection) setInternalRowSelection(next)
    },
    [rowSelection, isControlledSelection, onRowSelectionChange]
  )
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [sorting, setSorting] = React.useState<SortingState>([])

  // Use server-side pagination if provided, otherwise use client-side
  const isServerSidePagination = totalCount !== undefined && onPageChange !== undefined
  const [internalPagination, setInternalPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10
  })

  const pagination = isServerSidePagination
    ? {
        pageIndex: externalPageIndex ?? 0,
        pageSize: externalPageSize ?? 10
      }
    : internalPagination

  // Add select column if row selection is enabled
  const tableColumns = React.useMemo<ColumnDef<TData, TValue>[]>(() => {
    const safeColumns = columns ?? []
    if (!enableRowSelection) return safeColumns

    const selectAllPages =
      isServerSidePagination && totalCount !== undefined && onSelectAllPagesRequested != null
    const selectedCount = selectedRowIds?.length ?? 0
    const allPagesSelected = selectAllPages && selectedCount === totalCount
    const somePagesSelected =
      selectAllPages && selectedCount > 0 && selectedCount < (totalCount ?? 0)

    const selectColumn: ColumnDef<TData, TValue> = {
      id: 'select',
      header: ({ table }) => (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={
              selectAllPages
                ? allPagesSelected || (somePagesSelected && 'indeterminate')
                : table.getIsAllPageRowsSelected() ||
                  (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => {
              if (selectAllPages) {
                if (value) {
                  onSelectAllPagesRequested?.()
                } else {
                  onRowSelectionChange?.([])
                }
              } else {
                table.toggleAllPageRowsSelected(!!value)
              }
            }}
            aria-label="Select all"
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false
    }

    return [selectColumn, ...safeColumns]
  }, [
    columns,
    enableRowSelection,
    isServerSidePagination,
    totalCount,
    onSelectAllPagesRequested,
    selectedRowIds,
    onRowSelectionChange
  ])

  const handlePaginationChange: TableOptions<TData>['onPaginationChange'] = isServerSidePagination
    ? (updater) => {
        const nextPagination: PaginationState =
          typeof updater === 'function' ? updater(pagination) : updater

        onPageChange?.(nextPagination.pageIndex)
        onPageSizeChange?.(nextPagination.pageSize)
      }
    : setInternalPagination

  const tableConfig: TableOptions<TData> = {
    data: data ?? [],
    columns: tableColumns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      pagination
    },
    getRowId: getRowIdFn,
    enableRowSelection: enableRowSelection,
    // Sorting is opt-in per column (set enableSorting: true on a column def).
    // enableSortingRemoval gives the 3-state cycle: asc -> desc -> unsorted.
    enableSorting: false,
    enableSortingRemoval: true,
    onRowSelectionChange: handleRowSelectionChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues()
  }

  // Add pagination config based on whether it's server-side or client-side
  if (isServerSidePagination) {
    tableConfig.manualPagination = true
    if (totalCount !== undefined) {
      tableConfig.pageCount = Math.ceil(totalCount / (externalPageSize ?? 10))
    }
  } else {
    tableConfig.getPaginationRowModel = getPaginationRowModel()
  }

  const table = useReactTable(tableConfig)

  return (
    <div className="flex w-full flex-col justify-start gap-6">
      {/* Header with actions */}
      <div className="flex w-full flex-wrap items-center justify-end gap-2 px-3 lg:px-6">
        <div className="flex items-center gap-2">
          {enableColumnCustomization && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 shadow-sm hover:shadow">
                  <IconLayoutColumns className="size-4" />
                  <span className="hidden lg:inline">Customize Columns</span>
                  <span className="lg:hidden">Columns</span>
                  <IconChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {table
                  .getAllColumns()
                  .filter(
                    (column) => typeof column.accessorFn !== 'undefined' && column.getCanHide()
                  )
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    )
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {enableAddButton && onAddClick && (
            <Button variant="outline" size="sm" onClick={onAddClick}>
              <IconPlus />
              <span className="hidden lg:inline">{addButtonLabel}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="relative flex flex-col gap-4 overflow-auto">
        <div className="overflow-hidden rounded-xl border border-border/50 bg-card/50 shadow-sm">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-muted/70 backdrop-blur-sm">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort()
                    const sortDirection = header.column.getIsSorted()
                    const headerContent = header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())

                    return (
                      <TableHead key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder ? null : canSort ? (
                          <button
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                            className="-ml-1 inline-flex items-center gap-1 rounded px-1 py-0.5 text-left font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label={
                              sortDirection === 'asc'
                                ? 'Sorted ascending. Click to sort descending.'
                                : sortDirection === 'desc'
                                  ? 'Sorted descending. Click to clear sort.'
                                  : 'Not sorted. Click to sort ascending.'
                            }
                          >
                            {headerContent}
                            {sortDirection === 'asc' ? (
                              <IconChevronUp className="size-3.5 text-foreground" />
                            ) : sortDirection === 'desc' ? (
                              <IconChevronDown className="size-3.5 text-foreground" />
                            ) : (
                              <IconSelector className="size-3.5 text-muted-foreground/60" />
                            )}
                          </button>
                        ) : (
                          headerContent
                        )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table
                  .getRowModel()
                  .rows.map((row) => (
                    <TableRowWithSidekickLoader
                      key={row.id}
                      row={row}
                      sidekickEntityType={sidekickEntityType}
                      tableColumns={tableColumns}
                      onRowClick={onRowClick}
                    />
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={tableColumns.length} className="h-24 text-center">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          {enableRowSelection &&
            (isServerSidePagination && totalCount !== undefined
              ? (selectedRowIds?.length ?? 0) > 0
              : table.getFilteredSelectedRowModel().rows.length > 0) && (
              <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
                {isServerSidePagination && totalCount !== undefined
                  ? `${selectedRowIds?.length ?? 0} of ${totalCount} row(s) selected.`
                  : `${table.getFilteredSelectedRowModel().rows.length} of ${table.getFilteredRowModel().rows.length} row(s) selected.`}
              </div>
            )}
          {(!enableRowSelection ||
            (isServerSidePagination && totalCount !== undefined
              ? (selectedRowIds?.length ?? 0) === 0
              : table.getFilteredSelectedRowModel().rows.length === 0)) && (
            <div className="flex-1" />
          )}
          <div className="flex w-full items-center gap-8 lg:w-fit">
            <div className="hidden items-center gap-2 lg:flex">
              <Label htmlFor="rows-per-page" className="text-sm font-medium">
                Rows per page
              </Label>
              <Select
                value={String(table.getState().pagination.pageSize)}
                onValueChange={(value) => {
                  const newPageSize = Number(value)
                  if (isServerSidePagination) {
                    onPageSizeChange?.(newPageSize)
                    onPageChange?.(0)
                  } else {
                    table.setPageSize(newPageSize)
                    table.setPageIndex(0)
                  }
                }}
              >
                <SelectTrigger className="w-20" id="rows-per-page">
                  <SelectValue placeholder={String(table.getState().pagination.pageSize)} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 20, 50, 100].map((pageSize) => (
                    <SelectItem key={pageSize} value={String(pageSize)}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Pagination className="w-fit">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      if (isServerSidePagination) {
                        const currentPage = table.getState().pagination.pageIndex
                        if (currentPage > 0) {
                          onPageChange?.(currentPage - 1)
                        }
                      } else {
                        table.previousPage()
                      }
                    }}
                    className={!table.getCanPreviousPage() ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                {Array.from(
                  {
                    length:
                      isServerSidePagination && totalCount !== undefined
                        ? Math.ceil(totalCount / (externalPageSize ?? 10))
                        : table.getPageCount()
                  },
                  (_, i) => i + 1
                )
                  .filter((page) => {
                    const currentPage = table.getState().pagination.pageIndex + 1
                    const totalPages = table.getPageCount()
                    // Show first page, last page, current page, and pages around current
                    return (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    )
                  })
                  .map((page, index, array) => {
                    const currentPage = table.getState().pagination.pageIndex + 1
                    const showEllipsisBefore = index > 0 && page - array[index - 1] > 1
                    const showEllipsisAfter =
                      index < array.length - 1 && array[index + 1] - page > 1

                    return (
                      <React.Fragment key={page}>
                        {showEllipsisBefore && (
                          <PaginationItem>
                            <PaginationEllipsis />
                          </PaginationItem>
                        )}
                        <PaginationItem>
                          <PaginationLink
                            href="#"
                            isActive={page === currentPage}
                            onClick={(e) => {
                              e.preventDefault()
                              if (isServerSidePagination) {
                                onPageChange?.(page - 1)
                              } else {
                                table.setPageIndex(page - 1)
                              }
                            }}
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                        {showEllipsisAfter && index === array.length - 2 && (
                          <PaginationItem>
                            <PaginationEllipsis />
                          </PaginationItem>
                        )}
                      </React.Fragment>
                    )
                  })}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      if (isServerSidePagination) {
                        const currentPage = table.getState().pagination.pageIndex
                        const totalPages =
                          totalCount !== undefined
                            ? Math.ceil(totalCount / (externalPageSize ?? 10))
                            : 0
                        if (currentPage < totalPages - 1) {
                          onPageChange?.(currentPage + 1)
                        }
                      } else {
                        table.nextPage()
                      }
                    }}
                    className={!table.getCanNextPage() ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </div>
    </div>
  )
}
